import { AudioTrack, useRoomContext, useTracks } from '@livekit/components-react';
import { RemoteTrackPublication, RoomEvent, Track, type TrackPublication } from 'livekit-client';
import { useEffect, useState } from 'react';

import { decide, type AudioSource, type Listener } from '../listening';
import { LANGUAGE_ATTRIBUTE } from '../protocol';

interface Props {
  readonly listener: Listener;
}

function sourceOf(source: Track.Source): AudioSource {
  if (source === Track.Source.Microphone) return 'microphone';
  if (source === Track.Source.ScreenShareAudio) return 'screen_share_audio';
  return 'other';
}

/** Re-renders when any participant's attributes change (vm.lang decides what is played). */
function useAttributesVersion(): void {
  const room = useRoomContext();
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const bump = () => setVersion((current) => current + 1);
    room.on(RoomEvent.ParticipantAttributesChanged, bump);
    return () => {
      room.off(RoomEvent.ParticipantAttributesChanged, bump);
    };
  }, [room]);
  void version;
}

function Subscription({ publication, subscribe }: { publication: TrackPublication; subscribe: boolean }) {
  useEffect(() => {
    if (publication instanceof RemoteTrackPublication && publication.isSubscribed !== subscribe) {
      publication.setSubscribed(subscribe);
    }
  }, [publication, subscribe]);
  return null;
}

/**
 * Plays remote audio by the listener rule (ADR 001, src/listening.ts) instead of every track:
 * a speaker in another language is muted for me in voice mode and my language's
 * interpretation of them is played; interpretation tracks in other languages are never
 * subscribed. Replaces RoomAudioRenderer.
 */
export function ListenerAudio({ listener }: Props) {
  useAttributesVersion(); // a re-render recomputes every decision
  const tracks = useTracks([Track.Source.Microphone, Track.Source.ScreenShareAudio], { onlySubscribed: false });

  return (
    <>
      {tracks
        .filter((track) => track.publication.kind === Track.Kind.Audio)
        .map((track) => {
          const decision = decide(listener, {
            participantIdentity: track.participant.identity,
            participantIsAgent: track.participant.isAgent,
            participantLanguage: track.participant.attributes[LANGUAGE_ATTRIBUTE] ?? null,
            trackName: track.publication.trackName,
            source: sourceOf(track.publication.source),
          });
          return (
            <span key={track.publication.trackSid} data-track={track.publication.trackName} data-decision={decision.reason}>
              <Subscription publication={track.publication} subscribe={decision.subscribe} />
              {decision.subscribe && (
                // muted pauses the track server-side (setEnabled); volume 0 keeps the element silent meanwhile.
                <AudioTrack trackRef={track} muted={!decision.play} volume={decision.play ? 1 : 0} />
              )}
            </span>
          );
        })}
    </>
  );
}
